import { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { StatusBadge, Badge } from '@/components/ui/Badge';
import HealthRing from '@/components/ui/HealthRing';
import ChemBar from '@/components/ui/ChemBar';

function healthColor(h) {
  return h >= 85 ? 'var(--teal)' : h >= 70 ? 'var(--amber)' : 'var(--rose)';
}

function EstimatesCard({ customerId, openModal, sendEstimate, voidEstimate, estimates }) {
  const mine = estimates.filter(e => e.customerId === customerId);
  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Estimates</span>
        <button className="btn btn-primary btn-sm" onClick={() => openModal('extra_charge', { custId: customerId })}>
          + New Estimate
        </button>
      </div>
      <div className="card-body" style={{ paddingTop: 8 }}>
        {mine.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--t3)', textAlign: 'center', padding: '14px 0' }}>
            No estimates yet — create one to send the customer a tokenized approval link.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {mine.map(e => {
              const tone = e.status === 'approved' ? 'var(--teal)'
                : e.status === 'declined' || e.status === 'expired' || e.status === 'voided' ? 'var(--rose)'
                : e.status === 'sent' ? 'var(--cyan)'
                : 'var(--t3)';
              const link = `${window.location.origin}/estimate/${e.token}`;
              return (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--s2)', borderRadius: 7, fontSize: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>${Number(e.total).toLocaleString()}</div>
                    <div style={{ color: 'var(--t3)' }}>
                      {(e.lineItems || []).map(li => li.description).filter(Boolean).slice(0, 2).join(', ') || '—'}
                    </div>
                  </div>
                  <span style={{ color: tone, fontWeight: 600, textTransform: 'uppercase', fontSize: 10, letterSpacing: '.05em' }}>{e.status}</span>
                  {e.status === 'draft' && (
                    <button className="btn btn-ghost btn-sm" onClick={() => sendEstimate(e.id)}>Send</button>
                  )}
                  {(e.status === 'sent' || e.status === 'draft') && (
                    <button className="btn btn-ghost btn-sm" title="Copy link" onClick={() => navigator.clipboard?.writeText(link)}>
                      🔗
                    </button>
                  )}
                  {e.status !== 'voided' && e.status !== 'approved' && (
                    <button className="btn btn-ghost btn-sm" title="Void" onClick={() => voidEstimate(e.id)}>✕</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerDetail({ c, onClose, openModal }) {
  const { estimates, sendEstimate, voidEstimate } = useApp();
  if (!c) return null;
  const chem = c.chem;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Customer Detail</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <HealthRing score={c.health} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)' }}>{c.name}</div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>{c.addr}</div>
              <div style={{ fontSize: 12, color: 'var(--t3)' }}>{c.phone} · Customer since {c.since}</div>
              <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                {c.autopay && <Badge color="var(--cyan)" bg="var(--cyan-t)">Autopay</Badge>}
                {c.controller && (
                  <Badge color="var(--violet)" bg="var(--violet-t)">
                    {c.controller.charAt(0).toUpperCase() + c.controller.slice(1)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div style={{
            background: 'var(--s2)',
            borderRadius: 8,
            padding: 10,
            marginBottom: 12,
            fontSize: 12,
            color: 'var(--t3)',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 5,
          }}>
            <div>Gate: <strong style={{ color: 'var(--t1)' }}>{c.gateCode || '—'}</strong></div>
            <div>Pool: <strong style={{ color: 'var(--t1)' }}>{c.poolGal} gal</strong></div>
            <div>Tech: <strong style={{ color: 'var(--t1)' }}>{c.tech}</strong></div>
            <div>MRR: <strong style={{ color: 'var(--teal)' }}>${c.mrr}/mo</strong></div>
            {c.pets && c.pets !== 'None' && (
              <div style={{ gridColumn: 'span 2' }}>
                <strong style={{ color: 'var(--amber)' }}>{c.pets}</strong>
              </div>
            )}
          </div>

          {c.notes && (
            <div style={{
              background: 'var(--s2)',
              borderRadius: 8,
              padding: 10,
              marginBottom: 12,
              fontSize: 12,
              color: 'var(--t2)',
              lineHeight: 1.55,
              border: '1px solid var(--b1)',
            }}>
              {c.notes}
            </div>
          )}

          {chem && (
            <>
              <div style={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.07em',
                color: 'var(--t3)',
                marginBottom: 8,
              }}>
                Water Chemistry — Last Visit
              </div>
              <ChemBar param="cl"  label="Free Chlorine"    value={chem.cl} />
              <ChemBar param="ph"  label="pH"               value={chem.ph} />
              <ChemBar param="alk" label="Total Alkalinity"  value={chem.alk} />
              <ChemBar param="cya" label="CYA"              value={chem.cya} />
            </>
          )}
        </div>
      </div>

      <EstimatesCard
        customerId={c.id}
        openModal={openModal}
        sendEstimate={sendEstimate}
        voidEstimate={voidEstimate}
        estimates={estimates}
      />

      <div className="card">
        <div className="card-head"><span className="card-title">Actions</span></div>
        <div className="card-body" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('log_chem', { stopId: null, custId: c.id })}>
            Log Chemistry
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('add_invoice', { custId: c.id })}>
            Create Invoice
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('work_order', { custId: c.id })}>Work Order</button>
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('send_message', { custId: c.id })}>Send Message</button>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const { customers, selectedCustId, setSelectedCustId, openModal, search, setSearch } = useApp();
  const sel = selectedCustId ? customers.find(c => c.id === selectedCustId) : null;

  const q = search.toLowerCase();
  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(q) ||
    c.addr.toLowerCase().includes(q) ||
    c.tech.toLowerCase().includes(q)
  );

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Customers</div>
          <div className="psub">
            {customers.length} active accounts · ${customers.reduce((a, c) => a + c.mrr, 0).toLocaleString()} MRR
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('export', { entity: 'customers' })}>Export</button>
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('import_customers')}>
            ⬆ Import CSV
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => openModal('add_customer')}>
            + New Customer
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: sel ? '1fr 340px' : '1fr', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Search bar */}
          <div className="search-bar">
            <span style={{ color: 'var(--t3)' }}>🔍</span>
            <input
              type="text"
              placeholder="Search customers, addresses, technicians…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <Badge color="var(--t3)" bg="var(--s3)" style={{ cursor: 'pointer', borderColor: 'var(--b2)' }}>
              Filter
            </Badge>
          </div>

          {/* Customer table */}
          <div className="card">
            <div className="card-head">
              <span className="card-title">All Customers</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <Badge color="var(--teal)" bg="var(--teal-t)">Active</Badge>
                <Badge color="var(--amber)" bg="var(--amber-t)">At Risk</Badge>
              </div>
            </div>
            <div className="card-body-0" style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Tech</th>
                    <th>Pool Health</th>
                    <th>MRR</th>
                    <th>Next Visit</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedCustId(selectedCustId === c.id ? null : c.id)}
                      style={{ background: selectedCustId === c.id ? 'var(--s3)' : undefined }}
                    >
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div className="tbl-sub">Since {c.since}{c.autopay ? ' · Autopay' : ''}</div>
                      </td>
                      <td style={{ color: 'var(--t2)' }}>{c.addr}</td>
                      <td style={{ color: 'var(--t2)' }}>{c.tech}</td>
                      <td>
                        <span style={{ fontWeight: 700, color: healthColor(c.health) }}>{c.health}</span>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 12 }}>${c.mrr}</span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--t2)' }}>{c.nextVisit}</td>
                      <td><StatusBadge status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)' }}>No customers found</div>
              )}
            </div>
          </div>
        </div>

        {sel && (
          <CustomerDetail
            c={sel}
            onClose={() => setSelectedCustId(null)}
            openModal={openModal}
          />
        )}
      </div>
    </div>
  );
}
